import { useState } from 'react';

const faqs = [
  {
    question: 'What exactly do I get when I purchase?',
    answer: (
      <>
        You get the <strong className="text-[#E8603C]">complete Realtor Content Engine bundle</strong> delivered instantly: 30 Canva post templates, 15 Canva story templates, a 30-day content calendar, 30 done-for-you captions, 30 reel hook scripts, 15 story prompts, and a 10-point profile & bio checklist. Everything is delivered via email as a Google Drive link and a Canva template access link — <strong className="text-[#E8603C]">no waiting, no shipping</strong>.
      </>
    ),
  },
  {
    question: 'Do I need a paid Canva account to use this?',
    answer: (
      <>
        <strong className="text-[#E8603C]">No</strong> — a free Canva account works perfectly. All templates are built to be fully accessible and editable on Canva's free tier. You can swap your photo, name, brokerage, and brand colors without needing Canva Pro. If you already have Pro, you'll unlock a few extra font and brand kit features, but it's <strong className="text-[#E8603C]">completely optional</strong>.
      </>
    ),
  },
  {
    question: 'How do I customize the templates with my branding?',
    answer: (
      <>
        It's a simple 4-step process that takes about <strong className="text-[#E8603C]">30 minutes to set up your entire month</strong>:
        <ul className="mt-3 space-y-2">
          <li className="flex items-start gap-2">
            <span className="text-[#E8603C] font-bold">•</span>
            <span><strong>Step 1:</strong> Click the Canva access link in your delivery email — the templates open instantly.</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-[#E8603C] font-bold">•</span>
            <span><strong>Step 2:</strong> Click "Use Template" to create your own editable copy.</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-[#E8603C] font-bold">•</span>
            <span><strong>Step 3:</strong> Replace the placeholder photo, name, brokerage, and colors with yours. Each editable field is clearly labeled.</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-[#E8603C] font-bold">•</span>
            <span><strong>Step 4:</strong> Download as a JPG or PNG and post directly to Instagram. Repeat daily using the 30-day calendar.</span>
          </li>
        </ul>
      </>
    ),
  },
  {
    question: 'How do I use the 30 captions and reel hooks?',
    answer: (
      <>
        Every caption has <strong className="text-[#E8603C]">[brackets]</strong> showing you exactly where to fill in your details — your name, listing address, price, city, or custom stat. Just open the caption document, copy the Day 1 caption, replace the brackets, and paste it into Instagram. For the reel hooks: record yourself delivering the hook as the first line of your video, then pivot into your tip, tour, or story. The hook is the <strong className="text-[#E8603C]">scroll-stopper</strong> — everything after it is your content.
      </>
    ),
  },
  {
    question: 'How does the 30-day content calendar work?',
    answer: (
      <>
        The calendar maps out <strong className="text-[#E8603C]">every single day</strong> — what post type to publish, which caption to use, what format (Feed Post, Reel, Carousel, or Story), and the best time to post. Day 1 through Day 30 is fully planned. You can follow it exactly as written, or swap days around to fit your active listings. The goal is <strong className="text-[#E8603C]">consistency</strong> — showing up daily with a mix of content types is what grows your following and fills your DMs.
      </>
    ),
  },
  {
    question: "I don't have listings right now. Can I still use this?",
    answer: (
      <>
        <strong className="text-[#E8603C]">Absolutely</strong> — and this is actually the best time to use it. Most of the 30 post types are not listing-dependent. Buyer tips, seller tips, market updates, neighborhood spotlights, client testimonials, meet the agent posts, and reel hooks all work without an active listing. Consistent value content is what attracts buyers and sellers who don't know you yet — so when you do have a listing, you already have a <strong className="text-[#E8603C]">warm audience</strong> to post it to.
      </>
    ),
  },
  {
    question: 'Can I use this for markets outside of Houston?',
    answer: (
      <>
        <strong className="text-[#E8603C]">Yes — 100%</strong>. All location references in the templates and captions are in [brackets], so you just replace "Houston, TX" with your city. The content strategy, post types, captions, and reel hooks are built for <strong className="text-[#E8603C]">any real estate market in the US</strong>. Simply swap the city name and any local stat references and the entire system works exactly the same.
      </>
    ),
  },
  {
    question: 'What\'s the difference between this and other template packs on Etsy?',
    answer: (
      <>
        Most template packs give you the design and stop there. You still have to figure out what to write, what to post when, and how to script your reels. The Realtor Content Engine includes:
        <ul className="mt-3 space-y-2">
          <li className="flex items-start gap-2">
            <span className="text-[#E8603C]">✓</span>
            <span><strong className="text-[#E8603C]">30 written captions</strong> — copy, edit, post</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-[#E8603C]">✓</span>
            <span><strong className="text-[#E8603C]">30 reel hook scripts</strong> — the hardest part of video, already written</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-[#E8603C]">✓</span>
            <span><strong className="text-[#E8603C]">A full 30-day calendar</strong> — zero guesswork on what to post</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-[#E8603C]">✓</span>
            <span><strong className="text-[#E8603C]">Story prompts with sticker strategies</strong> — most packs don't touch Stories at all</span>
          </li>
        </ul>
        <p className="mt-3">It's a <strong className="text-[#E8603C]">complete content system</strong>, not just a visual asset pack.</p>
      </>
    ),
  },
  {
    question: 'Is there a refund policy?',
    answer: (
      <>
        Because this is an instant-access digital product, <strong className="text-[#E8603C]">all sales are final</strong> once the download link is delivered. You receive immediate access to all templates, captions, scripts, and resources — so we cannot offer refunds after delivery. If you have technical issues accessing your files or templates, contact us at <a href="mailto:support@realtorcontentengine.com" className="text-[#E8603C] underline hover:no-underline cursor-pointer">support@realtorcontentengine.com</a> and we'll help you get set up.
      </>
    ),
  },
  {
    question: 'Can I share this with my team or brokerage?',
    answer: (
      <>
        This is a <strong className="text-[#E8603C]">single-user license</strong> — one purchase covers one agent's personal use. If you want to share it with your team or brokerage, please contact us at <a href="mailto:team@realtorcontentengine.com" className="text-[#E8603C] underline hover:no-underline cursor-pointer">team@realtorcontentengine.com</a> for team licensing options. We offer discounted bulk pricing for brokerages and teams of 5+ agents.
      </>
    ),
  },
  {
    question: 'How do I receive the product after I buy?',
    answer: (
      <>
        <strong className="text-[#E8603C]">Instantly</strong>. The moment your payment goes through, Gumroad automatically emails you a download link. No waiting, no manual delivery. You'll have everything in your inbox within <strong className="text-[#E8603C]">60 seconds of purchase</strong>.
      </>
    ),
  },
  {
    question: 'Do I need design experience to use this?',
    answer: (
      <>
        <strong className="text-[#E8603C]">Zero design experience needed</strong>. Every template opens directly in Canva — the free version. You click on the placeholder, type your information, and download. If you can send a text message, you can use these templates.
      </>
    ),
  },
  {
    question: 'How long does it take to customize each template?',
    answer: (
      <>
        About <strong className="text-[#E8603C]">5 minutes per post</strong>. You're replacing bracketed placeholders with your details — your name, your listing, your price. The design, layout, caption, and strategy are already done for you.
      </>
    ),
  },
  {
    question: 'What if I\'m a brand new agent with no listings or testimonials yet?',
    answer: (
      <>
        This kit was built for you too. Over <strong className="text-[#E8603C]">80% of the 30 templates don't require active listings</strong> — buyer tips, seller tips, market updates, neighborhood spotlights, meet the agent posts, and engagement content work at any stage of your career.
      </>
    ),
  },
  {
    question: 'Can I use these templates on Facebook and LinkedIn too?',
    answer: (
      <>
        <strong className="text-[#E8603C]">Yes</strong>. While the templates are sized for Instagram, the captions, reel hooks, and content strategy work across all platforms. Many agents repurpose the same content across Instagram, Facebook, and LinkedIn with <strong className="text-[#E8603C]">zero extra effort</strong>.
      </>
    ),
  },
  {
    question: 'What if I have questions after I buy?',
    answer: (
      <>
        We've got you. Email us at <a href="mailto:support@realtorcontentengine.com" className="text-[#E8603C] underline hover:no-underline cursor-pointer">support@realtorcontentengine.com</a> and we respond within <strong className="text-[#E8603C]">24 hours</strong>. Every purchase comes with our commitment that you'll be able to use every single piece of this kit successfully.
      </>
    ),
  },
];

export default function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const toggleFAQ = (index: number) => {
    setOpenIndex(prev => (prev === index ? null : index));
  };

  return (
    <section className="py-24 px-6 bg-white">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-block mb-4">
            <span className="text-sm font-semibold text-[#E8603C] tracking-wider uppercase">
              FAQ
            </span>
          </div>
          <h2
            className="text-5xl font-bold text-[#1A1208] mb-4 leading-tight"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            Everything You Need to Know
          </h2>
          <p className="text-base text-[#8A7E74]">
            Quick answers before you buy — no fluff, no runaround.
          </p>
        </div>

        {/* FAQ Accordion */}
        <div className="border-t border-b border-[#E5DDD5]">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="border-b border-[#E5DDD5] last:border-b-0"
            >
              <button
                onClick={() => toggleFAQ(index)}
                className="w-full px-0 py-6 flex items-start justify-between text-left cursor-pointer hover:opacity-80 transition-opacity"
              >
                <span
                  className="text-base font-bold text-[#1A1208] pr-6"
                  style={{ fontFamily: 'DM Sans, sans-serif' }}
                >
                  {faq.question}
                </span>
                <div
                  className={`w-8 h-8 flex items-center justify-center flex-shrink-0 rounded-full transition-all duration-200 ${
                    openIndex === index
                      ? 'bg-[#E8603C] text-white'
                      : 'bg-[#FFF0EA] text-[#E8603C]'
                  }`}
                >
                  <span className="text-xl font-bold leading-none">
                    {openIndex === index ? '×' : '+'}
                  </span>
                </div>
              </button>

              <div
                className={`overflow-hidden transition-all duration-300 ${
                  openIndex === index ? 'max-h-[800px] pb-6' : 'max-h-0'
                }`}
              >
                <div
                  className="text-[0.92rem] text-[#8A7E74] leading-[1.75]"
                  style={{ fontFamily: 'DM Sans, sans-serif' }}
                >
                  {faq.answer}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
